import { useContext, useMemo } from "react";

import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { getFlowsheetCareElementValidationRegex } from "@/Flowsheets/Helpers/NumberCareElementHelper";
import { DecimalReviewedCareElement } from "@/Flowsheets/Models/CareElements/DecimalReviewedCareElement";
import { IntegerReviewedCareElement } from "@/Flowsheets/Models/CareElements/IntegerReviewedCareElement";
import { NumberInputWithDecimals } from "../../../../ConfigureFlowsheetModal/CareElementsConfigurationParts/NumberInputWIthDecimals";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface CareElementDateReviewedNumberProps {
    isEditable: boolean;
    label: string;
    side?: "row" | "column";
    element: DecimalReviewedCareElement | IntegerReviewedCareElement;
    onChange: (element: DecimalReviewedCareElement | IntegerReviewedCareElement) => void;
    regex?: RegExp;
    type?: "percentage" | "integer";
    unit?: string;
}

/**
 * Decimal input for the date reviewed care element.
 * @param props - The properties for the component.
 * @property {DecimalReviewedCareElement | IntegerReviewedCareElement} props.element - The care element to be displayed.
 * @property {function} props.onChange - Callback function to handle changes to the care element.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.label - The label for the care element.
 * @property {string} props.side - The side on which the care element is displayed (row or column).
 * @property {RegExp} props.regex - Optional regex for validating the decimal input.
 * @property {string} props.unit - Optional unit to display alongside the decimal value
 * @returns JSX.Element
 */
export default function CareElementDateReviewedNumber(props: Readonly<CareElementDateReviewedNumberProps>): JSX.Element {
    const { element, onChange, isEditable, label, side = "row", regex, unit, type } = props;
    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    const regularExpression = useMemo(
        () => regex ?? getFlowsheetCareElementValidationRegex(type),
        [regex, type]
    );

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
            isInputControl
        >
            {isEditable ? (
                <NumberInputWithDecimals
                    numberValue={element.Value}
                    onChange={value => {
                        const isValid = value !== null;

                        const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;

                        let dateToUse = null;

                        if (isValid) {
                            dateToUse = shouldAutoFillDate
                                ? encounterDateTimeWithPrecision
                                : element.DateReviewedWithPrecision;
                        }

                        onChange({ ...element, Value: isValid ? Number(value) : null, DateReviewedWithPrecision: dateToUse });
                    }}
                    regex={regularExpression}
                    unit={unit}
                    dataCy={`careElementDateReviewedNumberInput_${element.Name}`}
                />
            ) : (
                element.Value && <p>{element.Value} {unit}</p>
            )
            }
        </CareElementDateReviewedRow>
    );
}
